// FILE: src/components/Admin/AI/rag/ragAdminUtils.tsx
import React from 'react';
import { Chip, Paper, Stack, Typography } from '@mui/material';
import type { TuningColumn } from '../tuning/TuningAdminTable';
import { boolChip, safePreview } from '../tuning/TuningAdminTable';
import { DocExportLocalUploadRow } from '../../../../hooks/useDocExportLocalFiles';

export const idOf = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value);
  return Number(value.id ?? value.Id ?? value.value ?? null) || null;
};

export const labelOf = (value: any, fallback = '-'): string => {
  if (!value) return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.name || value.label || value.key || value.display_name || fallback;
};

export function statusChip(status?: string) {
  const s = String(status || '').toLowerCase();

  let color: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default';

  if (['active', 'ready', 'succeeded', 'healthy'].includes(s)) color = 'success';
  if (['standby', 'pending', 'validating'].includes(s)) color = 'info';
  if (['running', 'ingesting'].includes(s)) color = 'warning';
  if (['failed', 'cancelled', 'disabled'].includes(s)) color = 'error';

  return <Chip size="small" color={color} variant="outlined" label={status || 'unknown'} />;
}

export function dateLabel(value?: string | null) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export function jsonPreview(value: any, max = 180) {
  if (value === null || value === undefined || value === '') return '-';

  if (typeof value === 'string') {
    return safePreview(value, max);
  }

  try {
    return safePreview(JSON.stringify(value), max);
  } catch {
    return safePreview(String(value), max);
  }
}

export const SectionHeader: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
  subtitle?: string;
}> = ({ icon, title, description, subtitle }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icon}

      <div>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>

        {description || subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {description || subtitle}
          </Typography>
        ) : null}
      </div>
    </Stack>
  </Paper>
);

export const StatCard: React.FC<{
  label?: string;
  title?: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
  subtitle?: React.ReactNode;
}> = ({ label, title, value, caption, subtitle }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">
      {label || title}
    </Typography>

    <Typography variant="h5" fontWeight={700} mt={0.5}>
      {value}
    </Typography>

    {caption || subtitle ? (
      <Typography variant="body2" color="text.secondary" mt={1}>
        {caption || subtitle}
      </Typography>
    ) : null}
  </Paper>
);

export const productMappingColumns: TuningColumn[] = [
  {
    key: 'mapping',
    label: 'Mapping',
    sortable: false,
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          {row.product_name || labelOf(row.product, `Product ${row.product_id || row.product || '-'}`)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Purpose: {row.purpose_key || labelOf(row.purpose, 'default')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Group: {row.pipeline_group_key || labelOf(row.pipeline_group)}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'priority',
    label: 'Priority',
    width: 90,
    render: row => row.priority,
  },
  {
    key: 'is_default',
    label: 'Default',
    width: 100,
    render: row => boolChip(row.is_default),
  },
  {
    key: 'is_active',
    label: 'Active',
    width: 100,
    render: row => boolChip(row.is_active),
  },
  {
    key: 'meta_filters',
    label: 'Meta Filters',
    render: row => <Typography variant="caption">{jsonPreview(row.meta_filters)}</Typography>,
  },
];

export const routingMappingColumns: TuningColumn[] = [
  {
    key: 'scope_type',
    label: 'Scope',
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          {row.scope_label || `${row.scope_type}:${row.scope_id || 'default'}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.scope_type}:{row.scope_id || 'default'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Entity Constraint: {row.entity_type_key || row.entity_type || 'any'}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'purpose',
    label: 'Purpose',
    width: 160,
    render: row => row.purpose_key || row.purpose_label || row.purpose || 'default',
  },
  {
    key: 'pipeline_group',
    label: 'Pipeline Profile',
    width: 240,
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          {row.pipeline_group_name || labelOf(row.pipeline_group)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.pipeline_group_key || ''}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'priority',
    label: 'Priority',
    width: 90,
    render: row => row.priority,
  },
  {
    key: 'is_default',
    label: 'Default',
    width: 100,
    render: row => boolChip(row.is_default),
  },
  {
    key: 'is_active',
    label: 'Active',
    width: 100,
    render: row => boolChip(row.is_active),
  },
];

export const entityTypeColumns: TuningColumn[] = [
  {
    key: 'key',
    label: 'Entity Type',
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          {row.label || row.key}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.key}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'supports_entity_documentation',
    label: 'Entity Docs',
    width: 110,
    render: row => boolChip(row.supports_entity_documentation),
  },
  {
    key: 'supports_snackable_course',
    label: 'Snackable',
    width: 110,
    render: row => boolChip(row.supports_snackable_course),
  },
  {
    key: 'queue_task_type',
    label: 'Doc Task Type',
    width: 220,
    render: row => row.queue_task_type || '-',
  },
  {
    key: 'is_active',
    label: 'Active',
    width: 100,
    render: row => boolChip(row.is_active),
  },
];

export const requiredResourceColumns: TuningColumn[] = [
  {
    key: 'resource_type',
    label: 'Required Output',
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          {row.label || row.resource_type}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Entity: {row.entity_type_key || labelOf(row.entity_type)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Workflow: {row.workflow_type || 'entity_documentation'}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'is_required',
    label: 'Required',
    width: 110,
    render: row => boolChip(row.is_required),
  },
  {
    key: 'is_active',
    label: 'Active',
    width: 100,
    render: row => boolChip(row.is_active),
  },
  {
    key: 'sort_order',
    label: 'Order',
    width: 90,
    render: row => row.sort_order,
  },
];

export const ingestionRunColumns: TuningColumn[] = [
  {
    key: 'id',
    label: 'Run',
    render: row => (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={700}>
          Run {row.id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Group: {row.pipeline_group_key || labelOf(row.pipeline_group)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Pipeline: {row.pipeline_key || row.pipeline_name || row.pipeline || row.pipeline_slot_label || '-'}
        </Typography>
      </Stack>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    width: 130,
    render: row => statusChip(row.status),
  },
  {
    key: 'target_type',
    label: 'Target',
    width: 150,
    render: row => row.product_name || row.go_to_market_name || row.target_type,
  },
  {
    key: 'counts',
    label: 'Counts',
    width: 180,
    sortable: false,
    render: row => (
      <Typography variant="caption">
        up {row.uploaded_files_count || 0} / del {row.deleted_files_count || 0} / skip{' '}
        {row.skipped_files_count || 0}
      </Typography>
    ),
  },
  {
    key: 'created',
    label: 'Created',
    width: 180,
    render: row => dateLabel(row.created),
  },
];

export const canCancelDocExportLocalUpload = (
  row?: DocExportLocalUploadRow | null,
): boolean => {
  if (!row) return false;

  const status = String(row.status || '').toLowerCase();
  const retryable = Boolean(row.retryable);

  return (
    status === 'pending' ||
    status === 'skipped_duplicate' ||
    (status === 'failed' && retryable)
  );
};